import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { showingService } from '../../_services/showing.service';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

function fmtPreviewWindow(date, from, to) {
  if (!date || !from || !to) return null;
  const a = new Date(date + 'T' + from), b = new Date(date + 'T' + to);
  if (isNaN(a) || isNaN(b)) return null;
  const day = a.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const t = d => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${t(a)}–${t(b)}`;
}

function SectionHead({ num, title }) {
  return (
    <div className="ps-section-head">
      <span className="ps-num">{num}</span>
      <h3>{title}</h3>
      <span className="rule" />
    </div>
  );
}

export default function PostShowing({ history }) {
  const [form, setForm] = useState({
    address: '', city: '', state: '', zip: '', mls_id: '',
    date: '', time_from: '', time_to: '',
    duration_minutes: 30, fee: '', buyer_name: '',
    access_notes: '', notes: ''
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = e => {
    e.preventDefault();
    setError(null);

    if (!form.address || !form.city || !form.state) return setError('Address, city, and state are required.');
    if (!form.date || !form.time_from || !form.time_to) return setError('Pick a date and time window.');
    const start = new Date(form.date + 'T' + form.time_from);
    const end = new Date(form.date + 'T' + form.time_to);
    if (!(start < end)) return setError('The window end must be after its start.');
    if (end < new Date()) return setError('That time window is in the past.');
    const fee = parseFloat(form.fee);
    if (!(fee > 0)) return setError('Enter the fee you are offering.');

    setSaving(true);
    showingService.create({
      properties: [{
        address: form.address, city: form.city, state: form.state, zip: form.zip,
        mls_id: form.mls_id.trim() || undefined,
        access_notes: form.access_notes
      }],
      window_start: start.toISOString(),
      window_end: end.toISOString(),
      duration_minutes: parseInt(form.duration_minutes, 10) || 30,
      fee,
      buyer_name: form.buyer_name,
      notes: form.notes
    }).then(res => {
      setSaving(false);
      if (res.status === 'success') {
        history.push('/showings?posted=1');
      } else if (res.status === 'profile_incomplete') {
        setError(<>Complete your profile before posting showings. <Link to="/complete-profile">Go to your profile</Link>.</>);
      } else {
        setError(res.message || 'Something went wrong — try again.');
      }
    }).catch(() => { setSaving(false); setError('Network error — try again.'); });
  };

  const previewWindow = fmtPreviewWindow(form.date, form.time_from, form.time_to);
  const previewFee = parseFloat(form.fee);
  const perHour = previewFee > 0 && form.duration_minutes
    ? Math.round((previewFee / form.duration_minutes) * 60) : null;

  return (
    <div className="sh-page sh-page-wide">
      <h1>Post a showing</h1>
      <p className="sh-page-sub">Verified showing agents near the property are alerted the moment you post. First to accept covers it.</p>

      <div className="ps-layout">
        <form onSubmit={submit}>
          <div className="sh-card">
            <SectionHead num="1" title="The property" />
            <div className="sh-form-grid">
              <div className="sh-field full">
                <label>Property address</label>
                <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
                <span className="hint">Only the city and ZIP are shown publicly — the street address unlocks for the agent who accepts.</span>
              </div>
              <div className="sh-field">
                <label>City</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Austin" />
              </div>
              <div className="sh-field">
                <label>State</label>
                <select value={form.state} onChange={e => set('state', e.target.value)}>
                  <option value="">Select…</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sh-field">
                <label>ZIP</label>
                <input value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="78701" />
              </div>
              <div className="sh-field">
                <label>MLS ID <span style={{ fontWeight: 400 }}>(optional)</span></label>
                <input value={form.mls_id} onChange={e => set('mls_id', e.target.value)} placeholder="e.g. 4482915" />
              </div>
            </div>

            <SectionHead num="2" title="When" />
            <div className="sh-form-grid">
              <div className="sh-field">
                <label>Showing date</label>
                <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
              </div>
              <div className="sh-field">
                <label>Window opens</label>
                <input type="time" value={form.time_from} onChange={e => set('time_from', e.target.value)} />
              </div>
              <div className="sh-field">
                <label>Window closes</label>
                <input type="time" value={form.time_to} onChange={e => set('time_to', e.target.value)} />
              </div>
              <div className="sh-field">
                <label>Expected duration</label>
                <select value={form.duration_minutes} onChange={e => set('duration_minutes', e.target.value)}>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                </select>
              </div>
            </div>

            <SectionHead num="3" title="The offer" />
            <div className="sh-form-grid">
              <div className="sh-field">
                <label>Your fee (USD)</label>
                <div className="sh-fee-input">
                  <span>$</span>
                  <input type="number" min="1" step="1" value={form.fee} onChange={e => set('fee', e.target.value)} placeholder="45" />
                </div>
                <span className="hint">You set the price. Typical showings run $30–$75.</span>
              </div>
              <div className="sh-field">
                <label>Buyer first name <span style={{ fontWeight: 400 }}>(optional)</span></label>
                <input value={form.buyer_name} onChange={e => set('buyer_name', e.target.value)} placeholder="Sam" />
              </div>
            </div>

            <SectionHead num="4" title="For the showing agent" />
            <div className="sh-form-grid">
              <div className="sh-field full">
                <label>Access notes <span style={{ fontWeight: 400 }}>(shared only with the accepted agent)</span></label>
                <input value={form.access_notes} onChange={e => set('access_notes', e.target.value)} placeholder="Lockbox on the side gate, code from listing agent…" />
              </div>
              <div className="sh-field full">
                <label>Anything else the showing agent should know?</label>
                <textarea rows="3" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Second visit for this buyer — they'll have questions about the roof age." />
              </div>
            </div>

            {error ? <div className="sh-error">{error}</div> : null}

            <div className="sh-actions">
              <button type="submit" className="sh-btn sh-btn-primary" disabled={saving}>
                {saving ? 'Posting…' : 'Post showing'}
              </button>
              <span className="hint" style={{ color: 'var(--ng-muted)', fontSize: 13 }}>
                You'll be notified the second an agent accepts.
              </span>
            </div>
          </div>
        </form>

        <aside className="ps-preview">
          <div className="ps-preview-label">How agents will see it</div>
          <div className="tk-card">
            <div className="tk-main">
              <div className="tk-title-row">
                <span className="tk-title">
                  {[form.city, form.state].filter(Boolean).join(', ') || <span className="ghost">City, State</span>}
                </span>
              </div>
              <div className="tk-chips">
                <span className="tk-chip"><span className="ic">📅</span>{previewWindow || <span className="ghost">Pick a window</span>}</span>
                <span className="tk-chip"><span className="ic">⏱</span>{form.duration_minutes} min</span>
                {form.zip ? <span className="tk-chip"><span className="ic">📍</span>{form.zip}</span> : null}
              </div>
              {form.notes ? <div className="tk-notes">{form.notes}</div> : null}
            </div>
            <div className="tk-stub">
              <div>
                <span className="tk-fee">{previewFee > 0 ? `$${previewFee}` : <span className="ghost">$—</span>}</span>
                {perHour ? <div className="tk-rate">≈ ${perHour}/hr</div> : null}
              </div>
            </div>
          </div>
          <p className="ps-preview-note">
            The street address and access notes stay hidden until an agent accepts.
          </p>
        </aside>
      </div>
    </div>
  );
}
